import React, { useState } from 'react';
import { Button, Chip, Divider, IconButton, Dialog, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TitleTooltip from 'src/components/UI/TitleTooltip';
import {
  ExistingContentImprovementsDataV2,
  ExistingContentImprovementItemV2
} from '../../types/content-strategy-v2.type';

interface ContentImprovementProps {
  data?: ExistingContentImprovementsDataV2;
  page?: number;
  onPageChange?: (newPage: number) => void;
  isLoading?: boolean;
}

export const ContentImprovement: React.FC<ContentImprovementProps> = ({
  data,
  page = 1,
  onPageChange,
  isLoading = false
}) => {
  const [selectedItem, setSelectedItem] =
    useState<ExistingContentImprovementItemV2 | null>(null);

  const items = data?.items || [];
  const total = data?.total || 0;
  const limit = data?.limit || 3;
  const totalPages = Math.ceil(total / limit) || 1;

  const handleCopyAll = (item: ExistingContentImprovementItemV2) => {
    const parts: string[] = [];
    parts.push(`URL: ${item.url || ''}`);
    if (item.target_keyword) parts.push(`Keyword: ${item.target_keyword}`);
    parts.push(`Issue: ${item.current_issue || ''}`);
    if (item.recommended_fix?.summary) {
      parts.push(`Fix: ${item.recommended_fix.summary}`);
    }
    if (item.recommended_fix?.updates?.length) {
      parts.push(
        `Updates:\n${item.recommended_fix.updates
          .map((u) => `  - ${u}`)
          .join('\n')}`
      );
    }
    navigator.clipboard.writeText(parts.join('\n'));
  };

  // Extract a display title from the URL
  const getDisplayTitle = (item: ExistingContentImprovementItemV2) => {
    try {
      const url = new URL(item.url);
      const path = url.pathname.replace(/\/$/, '').split('/').pop();
      if (path) {
        return path
          .replace(/[-_]/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
      }
      return url.hostname;
    } catch {
      return item.url || 'Untitled Page';
    }
  };

  const getStatusChipClasses = (status?: string) => {
    const s = status?.toLowerCase();
    if (s === 'completed' || s === 'done' || s === 'fixed' || s === 'resolved') {
      return '!bg-[#ECFDF3] !text-[#027A48] !font-medium';
    }
    if (
      s === 'in_progress' ||
      s === 'in progress' ||
      s === 'processing' ||
      s === 'pending'
    ) {
      return '!bg-[#FEF0C7] !text-[#B54708] !font-medium';
    }
    if (s === 'open' || s === 'planned' || s === 'new') {
      return '!bg-[#EFF8FF] !text-[#175CD3] !font-medium';
    }
    if (s === 'ignored' || s === 'cancelled' || s === 'canceled') {
      return '!bg-[#F2F4F7] !text-[#667085] !font-medium';
    }
    return '!bg-[#F2F4F7] !text-[#344054] !font-medium';
  };

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 z-50 flex items-center justify-center backdrop-blur-[1px] rounded-xl">
          <CircularProgress size={40} sx={{ color: '#120242' }} />
        </div>
      )}
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-2">
        <TitleTooltip>
          <TitleTooltip.Text
            title="Existing Content Improvements"
            className="!text-[var(--color-text-secondary)] !font-medium !text-base"
          >
            <TitleTooltip.Tooltip
              tooltip={
                <p className="text-primary text-sm font-medium">
                  AI-identified recommendations to optimize and refresh your
                  existing pages to rank higher.
                </p>
              }
            />
          </TitleTooltip.Text>
        </TitleTooltip>

        {total > 0 && onPageChange && (
          <div className="flex items-center gap-2">
            <p className="text-primary text-base font-medium">
              {page}/{totalPages}
            </p>

            <div className="flex items-center">
              <IconButton
                size="small"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
              >
                <NavigateNextIcon />
              </IconButton>
            </div>
          </div>
        )}
      </div>

      <Divider />

      {items.length > 0 ? (
        items.map((item, index) => (
          <div key={item.id || index}>
            <div className="px-6 py-4 flex justify-between items-start">
              {/* LEFT */}
              <div className="flex-1 min-w-0">
                {/* TOP */}
                <div className="flex items-center gap-4 mb-3 flex-wrap">
                  <p className="font-semibold text-base text-[#1D2939] break-words">
                    {getDisplayTitle(item)}
                  </p>

                  {/* Keyword Pill */}
                  {item.target_keyword && (
                    <div className="flex items-center justify-between bg-[#F2F4F7] rounded px-3 h-[38px]">
                      <p className="text-sm text-[#344054] font-medium mr-2">
                        {item.target_keyword}
                      </p>

                      <IconButton
                        size="small"
                        onClick={() =>
                          navigator.clipboard.writeText(item.target_keyword!)
                        }
                      >
                        <img
                          src="/static/images/icons/copyIcon.svg"
                          style={{
                            width: '18px',
                            height: '18px',
                            objectFit: 'contain'
                          }}
                          alt="copyIcon"
                        />
                      </IconButton>
                    </div>
                  )}

                  {item.target_keyword_assigned === false && (
                    <Chip
                      label={
                        <span className="text-xs font-medium">
                          No Keyword Assigned
                        </span>
                      }
                      size="small"
                      className="!bg-[#FEE4E2] !text-[#D92D20] !font-medium"
                    />
                  )}
                </div>

                <div className="flex gap-3 flex-row">
                  {item.current_issue && (
                    <Chip
                      label={
                        <span className="text-xs font-medium whitespace-normal break-words">
                          {item.current_issue}
                        </span>
                      }
                      size="small"
                      className="!font-medium !h-auto !py-0.5 !max-w-full !rounded-md"
                      sx={{
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          display: 'block',
                          px: 1.5,
                          py: 0.5
                        }
                      }}
                    />
                  )}

                  {item.status && (
                    <Chip
                      label={
                        <span className="text-xs font-medium capitalize">
                          {item.status.replace('_', ' ')}
                        </span>
                      }
                      size="small"
                      className={getStatusChipClasses(item.status)}
                    />
                  )}
                </div>
              </div>

              {/* RIGHT */}
              <div className="flex flex-col lg:flex-row gap-4 ml-8">
                <Button
                  variant="outlined"
                  className="px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
                  onClick={() => handleCopyAll(item)}
                >
                  Copy All
                </Button>

                <Button
                  variant="outlined"
                  className="px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
                  onClick={() => setSelectedItem(item)}
                >
                  Details
                </Button>
              </div>
            </div>

            {index !== items.length - 1 && <Divider />}
          </div>
        ))
      ) : (
        <div className="px-6 py-8 text-center text-[#667085] text-sm font-normal">
          No data found
        </div>
      )}

      {/* Details Dialog */}
      <Dialog
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        disableScrollLock
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh'
          }
        }}
      >
        {selectedItem && (
          <div className="p-6 relative flex flex-col max-h-[90vh]">
            {/* Close Button */}
            <div className="absolute top-4 right-4 z-10">
              <IconButton
                size="small"
                onClick={() => setSelectedItem(null)}
                sx={{
                  color: '#667085',
                  '&:hover': { backgroundColor: '#F2F4F7' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>

            {/* Header */}
            <div className="pr-10 mb-4">
              <p className="text-xl font-semibold text-[#1D2939] leading-snug m-0">
                {getDisplayTitle(selectedItem)}
              </p>
            </div>

            <Divider />

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto pr-1 space-y-4 my-4">
              {/* URL */}
              {selectedItem.url && (
                <div>
                  <h4 className="font-semibold text-sm text-[#475467] mb-1.5">
                    URL
                  </h4>
                  <a
                    href={selectedItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline break-all font-normal"
                  >
                    {selectedItem.url}
                  </a>
                </div>
              )}

              {/* Current Issue */}
              {selectedItem.current_issue && (
                <div>
                  <h4 className="font-semibold text-sm text-[#475467] mb-1.5">
                    Current Issue
                  </h4>
                  <p className="text-sm text-[#344054] leading-relaxed whitespace-pre-wrap m-0 font-normal">
                    {selectedItem.current_issue}
                  </p>
                </div>
              )}

              {/* Recommended Fix */}
              {selectedItem.recommended_fix && (
                <div>
                  <h4 className="font-semibold text-sm text-[#475467] mb-1.5">
                    Recommended Fix
                  </h4>
                  {selectedItem.recommended_fix.summary && (
                    <p className="text-sm text-[#344054] leading-relaxed whitespace-pre-wrap mb-3 font-normal">
                      {selectedItem.recommended_fix.summary}
                    </p>
                  )}

                  {selectedItem.recommended_fix.updates &&
                    selectedItem.recommended_fix.updates.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {selectedItem.recommended_fix.updates.map(
                          (update, uIdx) => (
                            <div
                              key={uIdx}
                              className="p-3 bg-[#F9FAFB] rounded-lg border border-[#EAECF0]"
                            >
                              <p className="text-xs text-[#344054] leading-relaxed m-0 font-normal">
                                {update}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    )}

                  {/* Suggested Content */}
                  {selectedItem.recommended_fix.suggested_content && (
                    <div className="mt-3 space-y-2">
                      {selectedItem.recommended_fix.suggested_content.title && (
                        <div className="p-3 bg-[#F9FAFB] rounded-lg border border-[#EAECF0]">
                          <p className="font-semibold text-xs text-[#475467] mb-1">
                            Suggested Title
                          </p>
                          <p className="text-sm text-[#1D2939] m-0 font-normal">
                            {selectedItem.recommended_fix.suggested_content.title}
                          </p>
                        </div>
                      )}

                      {selectedItem.recommended_fix.suggested_content.h1 && (
                        <div className="p-3 bg-[#F9FAFB] rounded-lg border border-[#EAECF0]">
                          <p className="font-semibold text-xs text-[#475467] mb-1">
                            Suggested H1
                          </p>
                          <p className="text-sm text-[#1D2939] m-0 font-normal">
                            {selectedItem.recommended_fix.suggested_content.h1}
                          </p>
                        </div>
                      )}

                      {selectedItem.recommended_fix.suggested_content.meta_description && (
                        <div className="p-3 bg-[#F9FAFB] rounded-lg border border-[#EAECF0]">
                          <p className="font-semibold text-xs text-[#475467] mb-1">
                            Suggested Meta Description
                          </p>
                          <p className="text-sm text-[#1D2939] m-0 font-normal">
                            {selectedItem.recommended_fix.suggested_content.meta_description}
                          </p>
                        </div>
                      )}

                      {selectedItem.recommended_fix.suggested_content.internal_links &&
                        selectedItem.recommended_fix.suggested_content.internal_links.length > 0 && (
                          <div className="p-3 bg-[#F9FAFB] rounded-lg border border-[#EAECF0]">
                            <p className="font-semibold text-xs text-[#475467] mb-1">
                              Suggested Internal Links
                            </p>
                            {selectedItem.recommended_fix.suggested_content.internal_links.map(
                              (link, lIdx) => (
                                <p key={lIdx} className="text-xs text-[#344054] m-0 font-normal">
                                  <span className="font-medium">{link.anchor}</span>
                                  {' → '}
                                  <a
                                    href={link.target_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    {link.target_url}
                                  </a>
                                </p>
                              )
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ContentImprovement;
